import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Loader2, CheckCircle, AlertCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 bg-[#E11D48]/20 rounded-full flex items-center justify-center">
            <XCircle className="w-8 h-8 text-[#E11D48]" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Invalid Link</h2>
          <p className="text-[#71717A] text-sm mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password">
            <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-sm" data-testid="request-new-link-btn">
              Request New Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_ai-persona-connect/artifacts/d3pkba7d_copy_8D43DC47-40F7-432E-B350-F206250169DA.png" alt="Animus" className="w-12 h-12 object-contain" />
            <span className="font-bold text-2xl tracking-tight text-white font-['Unbounded']">Animus</span>
          </Link>
        </div>

        <div className="bg-[#0F1115] border border-white/10 rounded-sm p-8">
          {success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2" data-testid="reset-success-title">Password Reset!</h2>
              <p className="text-[#A1A1AA] text-sm mb-6">Your password has been updated. You can now sign in with your new password.</p>
              <Link to="/login">
                <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-sm" data-testid="go-to-login-btn">
                  Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-white mb-2 font-['Unbounded']">New password</h1>
              <p className="text-[#A1A1AA] text-sm mb-6">Enter your new password below.</p>

              {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-[#E11D48]/10 border border-[#E11D48]/20 rounded-sm text-[#E11D48] text-sm" data-testid="reset-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#A1A1AA]">New password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      required
                      minLength={6}
                      className="pl-10 bg-[#1A1D24] border-white/10 text-white placeholder:text-[#71717A] rounded-sm"
                      data-testid="reset-password-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-[#A1A1AA]">Confirm password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                    <Input
                      id="confirm"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      required
                      minLength={6}
                      className="pl-10 bg-[#1A1D24] border-white/10 text-white placeholder:text-[#71717A] rounded-sm"
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-sm py-3"
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...</>
                  ) : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
